import React, { useCallback, useState } from "react"
import PropTypes from "prop-types"
import Modal from "metabux/components/Modal/Modal"
import { Box, Typography, CircularProgress, Alert } from "@mui/material"
import { Button } from "metabux/components/Button/Button"
import { useAuth0 } from '@auth0/auth0-react'
import { useCurrentEulaQuery } from "@/redux/services/portal/user.api.js"
import urljoin from "url-join"
import { API_BASEPATH_PORTAL } from "@/config/env.config"

export const EulaConfirmationModal = ({
	open,
	onAccept,
	onDecline,
	...props
}) => {
	const { getAccessTokenSilently } = useAuth0();
	const [retryCount, setRetryCount] = useState(0);
	const [fopDownloading, setFopDownloading] = useState(false);
	const { data: eulaContent, isLoading, error, refetch } = useCurrentEulaQuery(undefined, {
		skip: !open, // Only fetch when modal is open
	});
	
	const handleRetry = useCallback(() => {
		setRetryCount(prev => prev + 1);
		refetch();
	}, [refetch]);

	const handleDownloadFop = useCallback(async () => {
		try {
			setFopDownloading(true);
			const url = urljoin(API_BASEPATH_PORTAL, 'shared-files', 'FairUsePolicy', 'download');
			const token = await getAccessTokenSilently({
				audience: 'https://portal-api'
			});
			
			// Fetch the file
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Authorization': 'Bearer' + ' ' + token,
					'X-Content-Type-Options': 'nosniff',
					'X-XSS-Protection': 1,
				},
			});
			
			if (response && !response.ok) {
				const error = await response.json();
				throw new Error(error.message || `Failed to download FOP: ${response.statusText}`);
			}
			
			// Extract filename from Content-Disposition header (now exposed by CORS)
			let filename = 'Fair-Use-Policy'; // Default fallback
			const contentDisposition = response.headers.get('Content-Disposition');
			if (contentDisposition) {
				// Parse filename from Content-Disposition: attachment; filename="filename.ext"
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}
			
			// Create blob and trigger download with the filename
			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(blobUrl);
		} catch (error) {
			console.error('Failed to download FOP:', error);
		} finally {
			setFopDownloading(false);
		}
	}, [getAccessTokenSilently]);

	const handleAccept = useCallback(() => {
		if (onAccept) onAccept();
	}, [onAccept]);

	const handleDecline = useCallback(() => {
		if (onDecline) onDecline();
	}, [onDecline]);

	// Prevent modal from being closed - user must accept or decline
	const handleModalClose = useCallback(() => {
		// No-op: Prevent closing the modal - user must click Accept or Decline
	}, []);

	return (
		<Modal
			open={open}
			onClose={handleModalClose} // Prevent closing by clicking outside or X button
			title="End User License Agreement"
			hideCloseButton={true}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 3,
					mt: 2,
					maxHeight: "70vh",
					overflowY: "auto"
				}}
			>
				<Typography variant="body1" sx={{ fontWeight: 600 }}>
					Please read and accept the End User License Agreement to continue.
				</Typography>

				<Box
					sx={{
						border: "1px solid #e0e0e0",
						borderRadius: "4px",
						p: 2,
						backgroundColor: "#f9f9f9",
						maxHeight: "400px",
						overflowY: "auto"
					}}
				>
					{isLoading && (
						<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
							<CircularProgress size={24} />
						</Box>
					)}
					{error && (
						<Alert 
							severity="error" 
							sx={{ mb: 2 }}
							action={
								retryCount < 3 && (
									<Button 
										variant="secondary" 
										size="small" 
										onClick={handleRetry}
										sx={{ ml: 2 }}
									>
										Retry
									</Button>
								)
							}
						>
							Failed to load EULA content. {retryCount < 3 ? 'Please try again.' : 'Please contact support if this issue persists.'}
						</Alert>
					)}
					{!isLoading && !error && eulaContent && (
						<Box
							component="div"
							sx={{
								whiteSpace: "pre-wrap", // Preserve line breaks and whitespace
								fontSize: "0.875rem", // Match body2 variant
								lineHeight: 1.5,
							}}
						>
							{eulaContent}
						</Box>
					)}
					{!isLoading && !error && !eulaContent && (
						<Typography variant="body2" color="text.secondary">
							No EULA content available.
						</Typography>
					)}
				</Box>

				<Box sx={{ display: "flex", gap: "10px", justifyContent: "space-between", mt: 2, alignItems: "center" }}>
					<Button 
						variant="secondary" 
						size="small"
						onClick={handleDownloadFop}
						disabled={fopDownloading}
						sx={{ minWidth: "auto" }}
					>
						{fopDownloading ? 'Downloading...' : 'Download Fair Use Policy'}
					</Button>
					<Box sx={{ display: "flex", gap: "10px" }}>
						<Button 
							variant="secondary" 
							color="black" 
							onClick={handleDecline}
							sx={{ minWidth: "120px" }}
						>
							I Decline
						</Button>
						<Button 
							variant="primary" 
							onClick={handleAccept}
							sx={{ minWidth: "120px" }}
						>
							I Accept
						</Button>
					</Box>
				</Box>
			</Box>
		</Modal>
	)
}

EulaConfirmationModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onAccept: PropTypes.func,
	onDecline: PropTypes.func,
}

export default EulaConfirmationModal

